import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import { createApi, FetchArgs } from '@reduxjs/toolkit/query/react'
import { REHYDRATE } from 'redux-persist'
import { baseQuery } from './auth'
import { useGetCalcIdFromRoute } from 'utils/hooks'
import { useSelector } from 'react-redux'
import { rootReducer, type AppDispatch } from 'store'
import sortBy from "lodash/sortBy";
import isEmpty from 'lodash/isEmpty'
import {informersApi} from "./informers";
import {ROUTES} from "../config/constants";
import {
  CALCULATION_VIEW_MODE_ERROR,
  CALCULATION_VIEW_MODE_ERROR_STATUS,
} from '../utils/helpers';

const enum SORTING_OPTIONS {
  CODE_DESC = 'code',
  CODE_ASC = '-code',
  NAME_DESC = 'name',
  NAME_ASC = '-name',
}

export const CALC_ID = "calcId";

export const getCalcId = () => localStorage.getItem(CALC_ID);

const CALCULATION_VIEW_PATH = `/${ROUTES.CABINET}/${ROUTES.CALCULATOR_VIEW}/`;
const CALCULATION_EDIT_PATH_PREFIX = `/${ROUTES.CABINET}/${ROUTES.CALCULATOR}/`;
const CALCULATION_VIEW_ALLOWED_MUTATION_PATHS = [
  '/broker/tariff/search/',
  '/calculate/',
  '/clone/',
  '/clone-for-support/',
  '/delivery/routes/',
  '/extra-services/search/',
  '/hscode-search/',
  '/refresh-currency-rates/',
  '/report/',
];
/** Мутации расчёта, разрешённые при блокировке заявкой (не меняют снимок). */
const CALCULATION_LOCKED_ALLOWED_MUTATION_PATHS = [
  '/report/',
];
const CALCULATION_MUTATION_METHODS = [ 'POST', 'PUT', 'PATCH', 'DELETE' ];

const shouldBlockCalculationMutation = (
  { args, getState }: CalculationMutationBlockerArgs,
): boolean => {
  if (typeof args === 'string') {
    return false;
  }

  const method = args.method?.toUpperCase() || 'GET';
  const isCalculationRequest = args.url.startsWith('calc-api/v4.0/');
  const isMutation = CALCULATION_MUTATION_METHODS.includes(method);

  const pathname = window.location.pathname;
  const isCalculatorEditPath = pathname.startsWith(CALCULATION_EDIT_PATH_PREFIX)
    && !pathname.startsWith(CALCULATION_VIEW_PATH);
  const lockedByActiveApplication = Boolean(getState().calcParams?.order?.locked_by_active_application);
  const isCloneMutation = args.url.includes('/clone/');
  const isLockedCalculator = isCalculatorEditPath && lockedByActiveApplication;
  const isCalculationMutation = isCalculationRequest && isMutation;

  if (isLockedCalculator && isCalculationMutation) {
    if (isCloneMutation) {
      return false;
    }
    if (CALCULATION_LOCKED_ALLOWED_MUTATION_PATHS.some((path) => args.url.includes(path))) {
      return false;
    }
    return true;
  }

  const isAllowedMutation = CALCULATION_VIEW_ALLOWED_MUTATION_PATHS.some((path) => args.url.includes(path));
  const blockedMutation = isCalculationMutation && !isAllowedMutation;

  if (!blockedMutation) {
    return false;
  }

  if (pathname.startsWith(CALCULATION_VIEW_PATH)) {
    return true;
  }

  return false;
}

const calcBaseQuery = baseQuery();

export const calcApi = createApi({
  baseQuery: async (args, api, extraOptions) => {
    if (shouldBlockCalculationMutation({ args, getState: api.getState as CalcMutationBlockerGetState })) {
      api.dispatch(notifyCalculationViewModeEditAttempt());

      return {
        error: {
          error: CALCULATION_VIEW_MODE_ERROR,
          status: CALCULATION_VIEW_MODE_ERROR_STATUS,
        },
      }
    }

    return calcBaseQuery(args, api, extraOptions);
  },
  reducerPath: 'calcApi',
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === REHYDRATE) return action.payload[reducerPath]
  },
  endpoints: (builder) => ({
    createOrder: builder.mutation<OrderParamsOutput, OrderParams>({
      query: (body) => ({ url: `calc-api/v4.0/order/`, method: 'POST', body }),
    }),
    orderById: builder.query<OrderParamsOutput, string>({
      query: (id) => id && ({
        url: `calc-api/v4.0/order/${id}/`,
        method: 'GET',
        meta: { suppressAccessDenied: true },
      }),
    }),
    updateOrder: builder.mutation<OrderParamsOutput, UpdateOrderParams>({
      query: ({ id, ...body }) => ({ url: `calc-api/v4.0/order/${id}/`, method: 'POST', body }),
    }),
    deleteOrder: builder.mutation<OrderParamsOutput, string>({
      query: (id) => ({ url: `calc-api/v4.0/order/${id}/`, method: 'DELETE' }),
    }),
    createInvoice: builder.mutation<InvoiceParamsOutput, InvoiceParamsInput>({
      query: ({id, ...body}) => ({ url: `calc-api/v4.0/order/${id}/invoice/`, method: 'POST', body }),
    }),
    invoicesByOrderId: builder.query<InvoiceParamsOutput[], string>({
      query: (id) => ({ url: `calc-api/v4.0/order/${id}/invoices/`, method: 'GET' }),
    }),
    updateInvoice: builder.mutation<InvoiceParamsOutput, InvoiceUpdateParamsInput>({
      query: ({orderId, invoiceId, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/invoice/${invoiceId}/`, method: 'POST', body }),
    }),
    removeInvoice: builder.mutation<InvoiceParamsOutput, InvoiceRemoveParamsInput>({
      query: ({orderId, invoiceId}) => ({ url: `calc-api/v4.0/order/${orderId}/invoice/${invoiceId}/`, method: 'DELETE' }),
    }),
    summaryByOrderId: builder.query<SummaryParamsOutput, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/packaging/`, method: 'GET' }),
    }),
    saveSummaryByOrderId: builder.mutation<SummaryParamsOutput, UpdateSummaryParamsInput>({
      query: ({orderId, isEmpty, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/packaging/`, method: 'POST', body }),
    }),
    changePrice: builder.query<CalculatePriceOutput, CalculatePriceInput>({
      query: (body) => ({ url: `calc-api/v4.0/calculate/price/`, body, method: 'POST' }),
    }),
    changePlaces: builder.query<CalculatePlacesOutput, CalculatePlacesInput>({
      query: (body) => ({ url: `calc-api/v4.0/calculate/places/`, body, method: 'POST' }),
    }),
    createProduct: builder.mutation<ProductParamsOutput, ProductParamsInput>({
      query: ({invoiceId, ...body}) => ({ url: `calc-api/v4.0/invoice/${invoiceId}/product/`, method: 'POST', body }),
    }),
    productsByInvoiceId: builder.query<ProductParamsOutput[], number>({
      query: (invoiceId) => ({ url: `calc-api/v4.0/invoice/${invoiceId}/products/`, method: 'GET' }),
    }),
    productsSearchByInvoiceId: builder.query<{ results: ProductListItem[], count: number }, ProductsSearchParamsInput>({
      query: ({ invoiceId, limit, page, ordering, query }) => ({ url: `calc-api/v4.0/invoice/${invoiceId}/products/search/`, params: { limit, page, ordering, query } }),
    }),
    productsDownloadExcelByInvoiceId: builder.query<{ blob: Blob; filename: string }, { invoiceId: number }>({
      query: ({ invoiceId }) => ({
        url: `calc-api/v4.0/invoice/${invoiceId}/products/download/`,
        responseHandler: async (response: any) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition");
          let filename = "export.xlsx"; // Default filename
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match){
              filename = match[1];
            }
          }

          return { blob, filename };
        },
        cache: "no-cache",
      }),
    }),
    productById: builder.query<ProductParamsOutput, ProductGetParamsInput>({
      query: ({invoiceId, productId}) => ({ url: `calc-api/v4.0/invoice/${invoiceId}/product/${productId}/`, method: 'GET' }),
    }),
    removeProduct: builder.mutation<void, ProductRemoveParamsInput>({
      query: ({ invoiceId, productId }) => ({ url: `calc-api/v4.0/invoice/${invoiceId}/product/${productId}/`, method: 'DELETE' }),
    }),
    updateProduct: builder.mutation<ProductParamsOutput, ProductUpdateParamsInput>({
      query: ({invoiceId, productId, ...body}) => ({ url: `calc-api/v4.0/invoice/${invoiceId}/product/${productId}/`, body, method: 'POST' }),
    }),
    suggestAdditionalFields: builder.mutation<SuggestAdditionalFieldsOutput, SuggestAdditionalFieldsInput>({
      query: ({ invoiceId, ...body }) => ({
        url: `calc-api/v4.0/invoice/${invoiceId}/product/suggest-additional-fields/`,
        method: 'POST',
        body,
      }),
    }),
    declarationsByOrderId: builder.query<DeclarationsParamsOutput[], string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/declarations/`, method: 'GET' }),
    }),
    declarationsSetCompleted: builder.mutation<DeclarationsParamsOutput[], string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/declarations/completed/`, method: 'POST' }),
    }),
    updateDeclaration: builder.mutation<DeclarationPostParamsOutput, DeclarationPostParamsInput>({
      query: ({orderId, code, country, invoiceId,  ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/${invoiceId}/declarations/${country}${code}/`, method: 'POST', body }),
    }),
    calculateDeclaration: builder.mutation<DeclarationCalculatePostParamsOutput, DeclarationPostParamsInput>({
      query: ({orderId, code, country, invoiceId, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/${invoiceId}/declarations/${country}${code}/calculate/`, method: 'POST', body }),
    }),
    declarationSummaryByOrderId: builder.query<DeclarationSummaryParamsOutput, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/customs/`, method: 'GET' }),
    }),
    getRoutesById: builder.mutation<DeliveryRouteParamsOutput, DeliveryRouteParamsInput>({
      query: ({ orderId, limit, page, city_name_from, city_name_to, ...body }) => ({ url: `calc-api/v4.0/order/${orderId}/delivery/routes/?limit=${limit || 20}&page=${page || 1}`, method: 'POST', body }),
    }),
    getOrderSummaryById: builder.query<OrderSummaryParamsOutput, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/summary/`, method: 'GET' }),
    }),
    refreshCurrencyRates: builder.mutation<RefreshCurrencyRatesResponse, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/refresh-currency-rates/`, method: 'POST' }),
    }),
    updateOrderSummaryById: builder.mutation<OrderSummaryInvoicesParamsInput, OrderSummaryInvoicesParamsInput>({
      query: ({orderId, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/summary/`, method: 'POST', body }),
    }),
    getOrderSummaryInvoicesById: builder.query<OrderSummaryInvoicesParamsOutput[], string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/summary-invoices/`, method: 'GET' }),
    }),
    chooseRouteByOrderId: builder.mutation<RouteParamsOutput, RouteParamsInput>({
      query: ({orderId, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/delivery/`, method: 'PUT', body }),
    }),
    addServicesToRouteByOrderId: builder.mutation<RouteParamsOutput, RouteParamsUpdateInput>({
      query: ({orderId, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/delivery/`, method: 'POST', body }),
    }),
    getRouteByOrderId: builder.query<Route, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/delivery/` }),
    }),
    getOrders: builder.mutation<OrdersParamsOutput, OrdersParamsInput>({
      query: ({ limit, page, ordering, min_total_amount, max_total_amount, search }) => ({ url: 'calc-api/v4.0/orders/', params: { limit, page, ordering, min_total_amount, max_total_amount, search } }),
    }),
    cloneCalculation: builder.mutation<OrderParamsOutput, string>({
      query: (id) => ({ url: `calc-api/v4.0/order/${id}/clone/`, method: 'POST'}),
    }),
    cloneCalculationForSupport: builder.mutation<CloneCalculationForSupportOutput, string>({
      query: (id) => ({ url: `calc-api/v4.0/order/${id}/clone-for-support/`, method: 'POST'}),
    }),
    getAdditionalServicesByOrderId: builder.query<AdditionalServicesParamsOutput, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/additional-services/`, }),
    }),
    setCompletedByOrderId: builder.mutation<CompletedParamsOutput, CompletedParamsOutput>({
      query: ({orderId, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/completed/`, method: 'POST', body }),
    }),
    updateBankCommissions: builder.mutation<BankCommissions[], BankCommissionsInput>({
      query: ({invoiceId, payments}) => ({ url: `calc-api/v4.0/order/invoice/${invoiceId}/banking_commissions/`, method: 'PUT', body: payments }),
    }),
    getBankCommissionsByOrderId: builder.query<BankCommissionsSummary, ParamsBankCommissionsOrderSummary>({
      query: ({orderId}) => ({ url: `calc-api/v4.0/order/${orderId}/invoices/banking_commissions/` }),
    }),
    setManualCertification: builder.mutation<ManualCertificationOutput, ManualCertificationInput>({
      query: ({orderId, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/certification/`, method: 'POST', body }),
    }),
    getExtraServicesSearchByOrderId: builder.mutation<ExtraServicesParamsOutput, ExtraServicesParamsInput>({
      query: ({ orderId, limit, page, ...body }) => ({ url: `calc-api/v4.0/order/${orderId}/extra-services/search/?limit=${limit || 30}&page=${page || 1}`, method: 'POST', body }),
    }),
    getExtraServicesCompaniesForFilter: builder.query<ExtraService[], string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/extra-services/companies/` }),
    }),
    setExtraServicesByOrderId: builder.mutation<ExtraServicesParamsOutput, SetExtraServicesParamsInput>({
      query: ({ orderId, ...body }) => ({ url: `calc-api/v4.0/order/${orderId}/extra-services/`, method: 'POST', body }),
    }),
    getExtraServicesByOrderId: builder.query<ExtraService[], string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/extra-services/` }),
    }),
    removeExtraServicesByOrderId: builder.mutation<ExtraServicesParamsOutput, SetExtraServicesParamsInput>({
      query: ({orderId, ...body}) => ({ url: `calc-api/v4.0/order/${orderId}/extra-services/`, method: 'DELETE', body  }),
    }),
    getBrokerServicesSearchByOrderId: builder.mutation<BrokerCompanyParamsOutput, BrokerServiceParamsInput>({
      query: ({ orderId, limit, page, ...body }) => ({ url: `calc-api/v4.0/order/${orderId}/broker/tariff/search/?limit=${limit || 30}&page=${page || 1}`, method: 'POST', body }),
    }),
    getBrokerServicesCompaniesForFilter: builder.query<BrokerCompanyInfo[], string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/broker/tariff/companies/` }),
    }),
    setBrokerServicesSearchByOrderId: builder.mutation<BrokerCompanyParamsOutput, SetBrokerServicesParamsInput>({
      query: ({ orderId, ...body }) => ({ url: `calc-api/v4.0/order/${orderId}/broker/tariff/`, method: 'POST', body }),
    }),
    getUserBrokerServicesByOrderId: builder.query<BrokerCompany, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/broker/tariff/` }),
    }),
    removeBrokerServicesByOrderId: builder.mutation<ExtraServicesParamsOutput, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/broker/tariff/`, method: 'DELETE'  }),
    }),
    getHscodeData: builder.mutation<HsCodeOutput, HsCodeInput>({
      query: ({ orderId, ...body }) => ({ url: `calc-api/v4.0/order/${orderId}/certification/tariff/hscode-search/`, method: 'POST', body }),
    }),
    getCertificationCompaniesForFilter: builder.query<CertificationCompanyParamsOutput, HsCodeInput & { search: string }>({
      query: ({ search, orderId, limit, page, ...body }) => ({
        url: `certification/v4.0/companies/`,
        method: 'POST',
        params: { search, limit: 50, page: 1 },
        body,
      }),
    }),
    setBrokerData: builder.mutation<BrokerOutput, BrokerInput>({
      query: ({ orderId, code, countryCode, ...body }) => ({ url: `calc-api/v4.0/order/${orderId}/certification/${countryCode}-${code}/`, method: 'POST', body }),
    }),
    getBrokerDataByCode: builder.query<BrokerOutput[], string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/certification/codes/` }),
    }),
    removeBrokerData: builder.mutation<string, BrokerRemoveInput>({
      query: ({orderId, code, countryCode, tech_reglament}) => ({ url: `calc-api/v4.0/order/${orderId}/certification/${countryCode}-${code}/`, params: { tech_reglament }, method: 'DELETE' }),
    }),
    getFinalReportData: builder.query<SummaryReportDataOutput, string>({
      query: (orderId) => ({ url: `calc-api/v4.0/order/${orderId}/report-data/`, method: 'GET' }),
    }),
    getFinalReport: builder.mutation<{ blob: Blob; filename: string }, SummaryReportInput>({
      query: ({orderId, ...body}) => ({
        url: `calc-api/v4.0/order/${orderId}/report/`,
        method: 'POST',
        body,
        responseHandler: async (response: any) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition");
          let filename = "report.xlsx";
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match){
              filename = match[1];
            }
          }

          return { blob, filename };
        },
        cache: "no-cache",
      }),
    }),
    orderDocumentsByOrderId: builder.query<CalculationDocument[], string>({
      query: (orderId) => orderId && ({
        url: `calc-api/v4.0/order/${orderId}/documents/`,
        method: 'GET',
      }),
    }),
    uploadOrderDocument: builder.mutation<
      CalculationDocument,
      { orderId: string; file: File; doc_kind: CalculationDocumentDocKind }
    >({
      query: ({ orderId, file, doc_kind }) => {
        const body = new FormData();
        body.append('file', file);
        body.append('title', file.name);
        body.append('doc_kind', doc_kind);
        return {
          url: `calc-api/v4.0/order/${orderId}/documents/`,
          method: 'POST',
          body,
        };
      },
    }),
    extractOrderDocument: builder.mutation<
      CalculationDocument,
      { orderId: string; documentId: number; doc_kind?: CalculationDocumentDocKind }
    >({
      query: ({ orderId, documentId, doc_kind }) => ({
        url: `calc-api/v4.0/order/${orderId}/documents/${documentId}/extract/`,
        method: 'POST',
        body: doc_kind ? { doc_kind } : {},
      }),
    }),
    orderDocumentById: builder.query<
      CalculationDocument,
      { orderId: string; documentId: number }
    >({
      query: ({ orderId, documentId }) => ({
        url: `calc-api/v4.0/order/${orderId}/documents/${documentId}/`,
        method: 'GET',
      }),
    }),
    applyOrderDocumentDraft: builder.mutation<ApplyOrderDocumentDraftOutput, ApplyOrderDocumentDraftInput>({
      query: ({ orderId, documentId, ...body }) => ({
        url: `calc-api/v4.0/order/${orderId}/documents/${documentId}/apply/`,
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useOrderByIdQuery,
  useInvoicesByOrderIdQuery,
  useLazyInvoicesByOrderIdQuery,
  useProductsByInvoiceIdQuery,
  useLazyProductsSearchByInvoiceIdQuery,
  useLazyProductsDownloadExcelByInvoiceIdQuery,
  useLazyProductByIdQuery,
  useLazyChangePriceQuery,
  useLazyChangePlacesQuery,
  useSummaryByOrderIdQuery,
  useLazySummaryByOrderIdQuery,
  useDeclarationsByOrderIdQuery,
  useLazyDeclarationsByOrderIdQuery,
  useDeclarationSummaryByOrderIdQuery,
  useLazyDeclarationSummaryByOrderIdQuery,
  useGetOrderSummaryByIdQuery,
  useLazyGetOrderSummaryByIdQuery,
  useGetOrderSummaryInvoicesByIdQuery,
  useLazyGetOrderSummaryInvoicesByIdQuery,
  useGetRouteByOrderIdQuery,
  useLazyGetRouteByOrderIdQuery,
  useGetAdditionalServicesByOrderIdQuery,
  useGetBankCommissionsByOrderIdQuery,
  useLazyGetBankCommissionsByOrderIdQuery,
  useGetExtraServicesCompaniesForFilterQuery,
  useGetExtraServicesByOrderIdQuery,
  useLazyGetExtraServicesByOrderIdQuery,
  useGetBrokerServicesCompaniesForFilterQuery,
  useGetCertificationCompaniesForFilterQuery,
  useLazyGetCertificationCompaniesForFilterQuery,
  useGetUserBrokerServicesByOrderIdQuery,
  useLazyGetFinalReportDataQuery,
  useLazyGetBrokerDataByCodeQuery,
  useGetBrokerDataByCodeQuery,
  useOrderDocumentsByOrderIdQuery,
} = calcApi

export const calcSlice = createSlice({
  name: 'calcParams',
  initialState: {
    order: {} as OrderParamsOutput,
    orders: {} as OrdersParamsOutput,
    params: {} as UserParams,
    paramsOrder: {} as OrdersParamsInput,
    paramsExtraServices: {} as OrdersParamsExtraServices,
    paramsBrokerServices: {} as OrdersParamsBrokerService,
    paramsHsCode: {} as HsCodeInput,
    productsList: {
      data: {
        results: [] as ProductListItem[],
        count: 0,
        summary: {} as ProductListItemSummary,
      },
      params: {
        limit: 50,
        page: 1,
        ordering: SORTING_OPTIONS.CODE_DESC,
      } as ProductsSearchParamsInput,
    },
    viewModeEditAttempt: false,
  },
  reducers: {
    notifyCalculationViewModeEditAttempt(state) {
      state.viewModeEditAttempt = true
    },
    clearCalculationViewModeEditAttempt(state) {
      state.viewModeEditAttempt = false
    },
    resetCalcSession(state) {
      Object.assign(state, calcSlice.getInitialState())
    },
    setUserExtraServicesIds(state, action: PayloadAction<number[]>) {
      state.order.userExtraServicesIds = action.payload
    },
    setLockedByActiveApplication(state) {
      state.order.locked_by_active_application = true
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      calcApi.endpoints.createOrder.matchFulfilled,
      (state, { payload }) => {
        state.order = { ...state.order, ...payload}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.orderById.matchFulfilled,
      (state, { payload }) => {
        state.order = { ...state.order, ...payload}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.updateOrder.matchFulfilled,
      (state, { payload }) => {
        state.order = {
          ...state.order,
          ...payload,
          invoices: state.order?.invoices
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.createInvoice.matchFulfilled,
      (state, { payload }) => {
        const invoices = state.order?.invoices
        state.order = {...state.order, invoices: invoices ? [ ...invoices, payload ] : [ payload ]}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.applyOrderDocumentDraft.matchFulfilled,
      (state, { payload }) => {
        const { invoice } = payload
        const invoices = state.order?.invoices || []
        const exists = invoices.some((item) => item.id === invoice.id)
        state.order = {
          ...state.order,
          invoices: exists
            ? invoices.map((item) => (item.id === invoice.id ? invoice : item))
            : [ ...invoices, invoice ],
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.invoicesByOrderId.matchFulfilled,
      (state, { payload }) => {
        state.order = {...state.order, invoices: payload}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.updateInvoice.matchFulfilled,
      (state, { payload }) => {
        const { id } = payload
        const invoices = state.order?.invoices
        const newInvoices = invoices?.map(invoice => invoice.id === id ? payload : invoice)
        state.order = {...state.order, invoices: newInvoices}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.productsByInvoiceId.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } }  }) => {
        const invoices = state.order?.invoices
        const newInvoices = invoices?.map(invoice => invoice.id === originalArgs ? {
          ...invoice,
          products: payload,
        } : invoice)
        state.order = {...state.order, invoices: newInvoices}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.productsSearchByInvoiceId.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } }  }) => {
        state.productsList = {
          ...state.productsList,
          // @ts-ignore
          data: payload,
          // @ts-ignore
          params: originalArgs,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.productById.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } }  }) => {

        const { productId, invoiceId } = originalArgs;

        const invoices = state.order?.invoices

        const getProducts = (products?: ProductParamsOutput[]) => {
          return products?.map(product => product.id === productId ? {
            ...payload,
          } : product)
        }

        const newInvoices = invoices?.map(invoice => invoice.id === invoiceId ? {
          ...invoice,
          products: getProducts(invoice.products),
        } : invoice)

        state.order = {...state.order, invoices: newInvoices}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getOrderSummaryById.matchFulfilled,
      (state, { payload }) => {
        state.order = {
          ...state.order,
          id: payload.id,
          summaryCompleted: payload.completed,
          summaryOrder: {
            ...state.order.summaryOrder,
            // @ts-ignore
            total_amount: payload?.summary?.total_amount,
            total_banking_commissions: payload?.summary?.total_banking_commissions,
            total_currency_control_fees: payload?.summary?.total_currency_control_fees,
            total_payment_amount: payload?.summary?.total_payment_amount,
            total_payment_amount_with_commissions: payload?.summary?.total_payment_amount_with_commissions,
          }
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.refreshCurrencyRates.matchFulfilled,
      (state, { payload }) => {
        const {
          completed,
          customs,
          delivery_route: deliveryRoute,
          summary,
          ...order
        } = payload.order

        state.order = {
          ...state.order,
          ...order,
          summaryCompleted: completed,
          summaryCustoms: {
            ...state.order.summaryCustoms,
            ...customs,
          },
          summaryOrder: {
            ...state.order.summaryOrder,
            ...summary,
          },
        }

        if (state.order.route) {
          state.order.route = {
            ...state.order.route,
            ...deliveryRoute,
          }
        }

        if (state.orders.results) {
          state.orders.results = state.orders.results.map((calculation) => (
            calculation.id === payload.order.id ? payload.order : calculation
          ))
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getOrderSummaryInvoicesById.matchFulfilled,
      (state, { payload }) => {
        state.order = {
          ...state.order,
          summaryInvoices: payload,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.setCompletedByOrderId.matchFulfilled,
      (state, { payload }) => {
        state.order = {
          ...state.order,
          summaryCompleted: payload
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.summaryByOrderId.matchFulfilled,
      (state, { payload }) => {
        state.order = {
          ...state.order,
          summaryOrder: {
            ...state.order.summaryOrder,
            ...payload,
            containers_user: !isEmpty(payload.containers_manual) ? payload.containers_manual : payload.containers
          },
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.saveSummaryByOrderId.matchFulfilled,
      (state, { payload,  meta: { arg: { originalArgs } }  }) => {
        state.order = {
          ...state.order,
          summaryOrder: originalArgs.isEmpty ? {} : {
            ...state.order.summaryOrder,
            ...payload,
            containers_user:  payload.containers_manual,
          }
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.declarationsByOrderId.matchFulfilled,
      (state, { payload }) => {
        state.order = {...state.order, declarations: payload}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.declarationSummaryByOrderId.matchFulfilled,
      (state, { payload }) => {
        state.order = {...state.order, summaryCustoms: payload }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getBrokerDataByCode.matchFulfilled,
      (state, { payload }) => {
        state.order = {...state.order, cert_codes: payload }
      }
    )
    builder.addMatcher(
      informersApi.endpoints.getInformers.matchFulfilled,
      (state, { payload }) => {
        const informers = {}
        payload.map((informer) => {
          const code = informer.code;
          // @ts-ignore
          informers[code] = informer.description
        })
        // @ts-ignore
        state.order = {...state.order, informers }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getRoutesById.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } }  }) => {
        state.order = { ...state.order, routes: payload }
        state.params = { ...state.params, ...originalArgs}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getRouteByOrderId.matchFulfilled,
      (state, { payload  }) => {
        state.order = { ...state.order, route: payload }
        state.params = { ...state.params }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.addServicesToRouteByOrderId.matchFulfilled,
      (state, { payload }) => {
        const routeData = payload.route ? { ...state.order.route } : undefined;
        const newRouteData = {
          ...routeData,
          selected_services: payload.services,
        };
        // @ts-ignore
        state.order = { ...state.order, route: newRouteData }
        state.params = { ...state.params }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getOrders.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } }  }) => {
        state.orders = payload
        state.paramsOrder = {
          ...state.paramsOrder,
          ...originalArgs,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.cloneCalculation.matchFulfilled,
      (state, { payload }) => {
        state.order = { ...state.order, ...payload}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getAdditionalServicesByOrderId.matchFulfilled,
      (state, { payload }) => {
        state.order = {...state.order, additionalServices: payload}
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getBankCommissionsByOrderId.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } } }) => {

        // @ts-ignore
        if (originalArgs.isChanging){

          if (!state.order.bankCommissions){
            return;
          }

          const getBankCommissions = (invoice: BankCommissionsInvoice) => {
            if (originalArgs.isRemove) {
              // @ts-ignore
              return invoice.banking_commissions.filter(item => item.id !== payload.id)
            }

            // @ts-ignore
            if (payload.id){
              return invoice.banking_commissions.map((bankingCommission) =>
              // @ts-ignore
                bankingCommission.id === payload.id ? payload: bankingCommission
              )
            }

            // @ts-ignore
            if (payload.indexItem){
              return invoice.banking_commissions.map((bankingCommission, index) =>
              // @ts-ignore
                index === payload.indexItem ? payload: bankingCommission
              )
            }

            return [ ...invoice.banking_commissions, payload ]
          }
          const newBankCommissions = state.order.bankCommissions?.invoices?.map(
            invoice => invoice.invoice_num === originalArgs.invoiceNumber ? {
              ...invoice,
              banking_commissions: getBankCommissions(invoice),
            } : invoice)

          state.order = {
            ...state.order,
            // @ts-ignore
            bankCommissions: {
              ...state.order.bankCommissions,
              invoices: newBankCommissions
            }
          }
        } else {
          state.order = {...state.order, bankCommissions: payload}
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.setManualCertification.matchFulfilled,
      (state, { payload }) => {
        const summaryCustomsNew = {
          ...state.order.summaryCustoms,
          ...payload
        }
        // @ts-ignore
        state.order = { ...state.order, summaryCustoms: summaryCustomsNew }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getExtraServicesSearchByOrderId.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } }  }) => {
        state.order = { ...state.order, extraServices: payload.results }
        state.paramsExtraServices = {
          ...originalArgs,
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getExtraServicesByOrderId.matchFulfilled,
      (state, { payload }) => {

        const serviceIds = payload.flatMap(company =>
          company.services.map(service => service.id)
        )

        state.order = {
          ...state.order,
          userExtraServices: payload,
          userExtraServicesIds: serviceIds
        }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getUserBrokerServicesByOrderId.matchFulfilled,
      (state, { payload }) => {
        state.order = { ...state.order, userBrokerService: payload }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.removeBrokerServicesByOrderId.matchFulfilled,
      (state) => {
        state.order = { ...state.order, userBrokerService: undefined }
      }
    )
    builder.addMatcher(
      calcApi.endpoints.getBrokerServicesSearchByOrderId.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } }  }) => {
        state.order = { ...state.order, brokerServices: payload.results }
        state.paramsBrokerServices = {
          ...originalArgs,
        }
      }
    )

    builder.addMatcher(
      calcApi.endpoints.getHscodeData.matchFulfilled,
      (state, { payload, meta: { arg: { originalArgs } } }) => {
        const { orderId, ...argsWithoutOrderId } = originalArgs;
        state.order = {
          ...state.order,
          cert_centers: payload,
        }
        state.paramsHsCode = {
          ...argsWithoutOrderId,
        }
      }
    )
  },
})

export const useCreateOrderMutation = () => {
  return calcApi.useCreateOrderMutation({ fixedCacheKey: 'createOrder' })
}

export const useUpdateOrderMutation = () => {
  return calcApi.useUpdateOrderMutation({ fixedCacheKey: 'updateOrder' })
}

export const useDeleteOrderMutation = () => {
  return calcApi.useDeleteOrderMutation({ fixedCacheKey: 'deleteOrder' })
}

export const useCreateInvoiceMutation = () => {
  return calcApi.useCreateInvoiceMutation({ fixedCacheKey: 'createInvoice' })
}

export const useUpdateInvoiceMutation = () => {
  return calcApi.useUpdateInvoiceMutation({ fixedCacheKey: 'updateInvoice' })
}

export const useRemoveInvoiceMutation = () => {
  return calcApi.useRemoveInvoiceMutation({ fixedCacheKey: 'removeInvoice' })
}

export const useCreateProductMutation = () => {
  return calcApi.useCreateProductMutation({ fixedCacheKey: 'createProduct' })
}

export const useRemoveProductMutation = () => {
  return calcApi.useRemoveProductMutation({ fixedCacheKey: 'removeProduct' })
}

export const useUpdateProductMutation = () => {
  return calcApi.useUpdateProductMutation({ fixedCacheKey: 'updateProduct' })
}

export const useSuggestAdditionalFieldsMutation = () => {
  return calcApi.useSuggestAdditionalFieldsMutation()
}

export const useGetRoutesMutation = () => {
  return calcApi.useGetRoutesByIdMutation({ fixedCacheKey: 'getRoutesById' })
}

export const useUpdateSummaryByOrderIdMutation = () => {
  return calcApi.useSaveSummaryByOrderIdMutation({ fixedCacheKey: 'saveSummaryByOrderId' })
}

export const useUpdateDeclarationMutation = () => {
  return calcApi.useUpdateDeclarationMutation({ fixedCacheKey: 'updateDeclaration' })
}

export const useCalculateDeclarationMutation = () => {
  return calcApi.useCalculateDeclarationMutation({ fixedCacheKey: 'calculateDeclaration' })
}

export const useChooseRouteByOrderIdMutation = () => {
  return calcApi.useChooseRouteByOrderIdMutation({ fixedCacheKey: 'chooseRouteByOrderId' })
}

export const useAddServicesToRouteByOrderIdMutation = () => {
  return calcApi.useAddServicesToRouteByOrderIdMutation({ fixedCacheKey: 'addServicesToRouteByOrderId' })
}

export const useGetOrdersMutation = () => {
  return calcApi.useGetOrdersMutation({ fixedCacheKey: 'getOrders' })
}

export const useCloneCalculationMutation = () => {
  return calcApi.useCloneCalculationMutation({ fixedCacheKey: 'cloneCalculation' })
}

export const useCloneCalculationForSupportMutation = () => {
  return calcApi.useCloneCalculationForSupportMutation({ fixedCacheKey: 'cloneCalculationForSupport' })
}

export const useSetCompletedByOrderIdMutation = () => {
  return calcApi.useSetCompletedByOrderIdMutation({ fixedCacheKey: 'setCompletedByOrderId' })
}

export const useUpdateBankCommissionsMutation = () => {
  return calcApi.useUpdateBankCommissionsMutation({ fixedCacheKey: 'updateBankCommissions' })
}

export const useSetManualCertificationMutation = () => {
  return calcApi.useSetManualCertificationMutation({ fixedCacheKey: 'setManualCertification' })
}

export const useGetExtraServicesSearchByOrderIdMutation = () => {
  return calcApi.useGetExtraServicesSearchByOrderIdMutation({ fixedCacheKey: 'getExtraServicesSearchByOrderId' })
}

export const useSetExtraServicesByOrderIdMutation = () => {
  return calcApi.useSetExtraServicesByOrderIdMutation({ fixedCacheKey: 'setExtraServicesByOrderId' })
}

export const useRemoveExtraServicesByOrderIdMutation = () => {
  return calcApi.useRemoveExtraServicesByOrderIdMutation({ fixedCacheKey: 'removeExtraServicesByOrderId' })
}

export const useGetBrokerServicesSearchByOrderIdMutation = () => {
  return calcApi.useGetBrokerServicesSearchByOrderIdMutation({ fixedCacheKey: 'getBrokerServicesSearchByOrderId' })
}

export const useSetBrokerServicesSearchByOrderIdMutation = () => {
  return calcApi.useSetBrokerServicesSearchByOrderIdMutation({ fixedCacheKey: 'setBrokerServicesSearchByOrderId' })
}

export const useRemoveBrokerServicesByOrderIdMutation = () => {
  return calcApi.useRemoveBrokerServicesByOrderIdMutation({ fixedCacheKey: 'removeBrokerServicesByOrderId' })
}

export const updateOrderSummaryByIdMutation = () => {
  return calcApi.useUpdateOrderSummaryByIdMutation({ fixedCacheKey: 'updateOrderSummaryById' })
}

export const useRefreshCurrencyRatesMutation = () => {
  return calcApi.useRefreshCurrencyRatesMutation({ fixedCacheKey: 'refreshCurrencyRates' })
}

export const getHscodeDataMutation = () => {
  return calcApi.useGetHscodeDataMutation({ fixedCacheKey: 'getHscodeData' })
}

export const setBrokerDataMutation = () => {
  return calcApi.useSetBrokerDataMutation({ fixedCacheKey: 'setBrokerData' })
}

export const removeBrokerDataMutation = () => {
  return calcApi.useRemoveBrokerDataMutation({ fixedCacheKey: 'removeBrokerData' })
}

export const getFinalReportMutation = () => {
  return calcApi.useGetFinalReportMutation({ fixedCacheKey: 'getFinalReport' })
}

export const useUploadOrderDocumentMutation = () => {
  return calcApi.useUploadOrderDocumentMutation({ fixedCacheKey: 'uploadOrderDocument' })
}

export const useExtractOrderDocumentMutation = () => {
  return calcApi.useExtractOrderDocumentMutation({ fixedCacheKey: 'extractOrderDocument' })
}

export const useLazyOrderDocumentByIdQuery = () => {
  return calcApi.useLazyOrderDocumentByIdQuery()
}

export const useApplyOrderDocumentDraftMutation = () => {
  return calcApi.useApplyOrderDocumentDraftMutation({ fixedCacheKey: 'applyOrderDocumentDraft' })
}

export const refetchOrderAfterDocumentApply = (orderId: string, invoiceId?: number) => {
  return (dispatch: AppDispatch) => Promise.all([
    dispatch(calcApi.endpoints.invoicesByOrderId.initiate(orderId, { forceRefetch: true })),
    dispatch(calcApi.endpoints.summaryByOrderId.initiate(orderId, { forceRefetch: true })),
    dispatch(calcApi.endpoints.getOrderSummaryById.initiate(orderId, { forceRefetch: true })),
    dispatch(calcApi.endpoints.declarationSummaryByOrderId.initiate(orderId, { forceRefetch: true })),
    dispatch(calcApi.endpoints.orderDocumentsByOrderId.initiate(orderId, { forceRefetch: true })),
    ...(invoiceId
      ? [ dispatch(calcApi.endpoints.productsByInvoiceId.initiate(invoiceId, { forceRefetch: true })) ]
      : []),
  ])
}

export const useGetOrder = () => {
  const orderId = useGetCalcIdFromRoute()
  return useOrderByIdQuery(orderId, { skip: !orderId })
}

export const useGetInvoices = () => {
  const orderId = useGetCalcIdFromRoute()
  const invoices = useInvoicesByOrderIdQuery(orderId, { skip: !orderId })?.data
  return sortBy(invoices, 'id')
}

export const useInvoicesData = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.invoices)
}

export const useSummary = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.summaryOrder)
}

export const useParamsData = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.params)
}

export const useOrderParamsData = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.paramsOrder)
}

export const useUpdateSummaryByOrderId = ({orderId, summary} : SummaryParamsUpdate) => {
  return calcApi.util.upsertQueryData('summaryByOrderId', orderId, summary)
}

export const useUpdateProductById = (data: ProductParamsOutput) => {
  return calcApi.util.upsertQueryData('productById', {invoiceId: data.invoice_id, productId: data.id}, data)
}

export const useUpdateBankCommissionsByInvoiceId = ({ orderId, data, invoiceNumber, isRemove}: ParamsBankCommissionsOrderSummary) => {
  const args = {
    orderId,
    invoiceNumber,
    isChanging: true,
    isRemove,
  }
  // @ts-ignore
  return calcApi.util.upsertQueryData('getBankCommissionsByOrderId', args, data)
}

export const useResetOrderId = (orderId: string) => {
  // @ts-ignore
  return calcApi.util.upsertQueryData('orderById', orderId, {
    id: undefined,
    order_name: undefined,
    base_currency: '',
    comment: undefined,
    payer_type: undefined,
    summaryInvoices: undefined,
    routes: undefined,
    bankCommissions: undefined,
    certification: undefined,
    paramsHsCode: undefined,
    current_cert_centers: undefined,
    cert_centers_search: undefined,
    extraServices:  undefined,
    userExtraServices: undefined,
    brokerServices: undefined,
    userBrokerService:undefined,
    num: undefined,
    created_at: undefined,
    updated_at: undefined,
    status : undefined,
    invoices: undefined,
    declarations: undefined,
    summaryCustoms: {},
    summaryOrder: {
      // @ts-ignore
      total_amount: undefined,
      total_payment_amount: undefined,
      total_banking_commissions: undefined,
      total_currency_control_fees: undefined,
      total_payment_amount_with_commissions: undefined
    },
    summaryCompleted: {
      additional_services: false,
      banking_commissions: false,
      broker: false,
      certification: false,
      common: false,
      customs: false,
      delivery: false,
      extra_services: false,
      packaging: false,
    },
    additionalServices: undefined,
    route: undefined,
  })
}


export const useOrder = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order)
}

export const useSummaryOrder = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.summaryOrder)
}

export const useSummaryCustoms = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.summaryCustoms)
}

export const useSummaryCompleted = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.summaryCompleted)
}

export const useSummaryInvoices = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.summaryInvoices)
}

export const useInvoices = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.invoices)
}

export const useBankCommissions = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.bankCommissions);
}

export const useBankCommissionsByInvoiceId = (id: string) => {
  const bankCommissions = useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.bankCommissions?.invoices);
  return bankCommissions?.find((item: BankCommissionsInvoice) => item.invoice_num === id);
}

export const useCurrentRoute = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.route)
}

export const useAdditionalServices = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.additionalServices)
}

export const useParamsHsCode = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.paramsHsCode)
}

export const useDeclarations = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.declarations)
}

export const useCertificationCenters = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.cert_centers)
}

export const useParamsExtraServicesData = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.paramsExtraServices)
}

export const useUserExtraServices = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.userExtraServices)
}

export const useParamsBrokerServices = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.paramsBrokerServices)
}

export const useUserBrokerService = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.userBrokerService)
}

export const useProductsList = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.productsList)
}

export const useBaseCurrency = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.base_currency || 'USD')
}

export const useCertCodes = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.cert_codes)
}

export const INITIAL_DATA_REQUEST = {
  limit: 50,
  page: 1,
  ordering: SORTING_OPTIONS.NAME_ASC
}

export const useUpdateProductsList = () => {
  return calcApi.util.upsertQueryData('productsSearchByInvoiceId', INITIAL_DATA_REQUEST, {
    results: [],
    count: 0
  })
}

export const useExtraServicesId = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.order.userExtraServicesIds)
}

export const useCalculationViewModeEditAttempt = () => {
  return useSelector(({ calcParams }: ReturnType<typeof rootReducer>) => calcParams.viewModeEditAttempt)
}

export const {
  clearCalculationViewModeEditAttempt,
  notifyCalculationViewModeEditAttempt,
  resetCalcSession,
  setLockedByActiveApplication,
  setUserExtraServicesIds,
} = calcSlice.actions

export const resetCalculationSession = (dispatch: AppDispatch) => {
  localStorage.removeItem(CALC_ID);
  dispatch(resetCalcSession());
  dispatch(calcApi.util.resetApiState());
};
